import type { TaskComment, TaskCommentFieldAnchor } from '../types';

const REQUIREMENT_FIELDS = new Set([
  'requirement',
  'sources',
  'quote',
  'justification',
  'score',
]);

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/** Runtime guard for Firestore data and other untrusted comment payloads. */
export function isTaskCommentFieldAnchor(value: unknown): value is TaskCommentFieldAnchor {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const candidate = value as Record<string, unknown>;
  if (!isNonEmptyString(candidate.requirementId)) return false;
  if (candidate.type === 'rubric-requirement') {
    return typeof candidate.field === 'string' && REQUIREMENT_FIELDS.has(candidate.field);
  }
  return candidate.type === 'rubric-grade'
    && isNonEmptyString(candidate.modelKey)
    && candidate.field === 'grade';
}

/**
 * Stable in-memory key for a field anchor. This is intentionally independent
 * of the current array index so comments survive rubric reorganization.
 */
export function taskCommentFieldAnchorKey(
  anchor: TaskCommentFieldAnchor | null | undefined,
): string | null {
  if (!isTaskCommentFieldAnchor(anchor)) return null;
  const requirement = encodeURIComponent(anchor.requirementId);
  if (anchor.type === 'rubric-requirement') {
    return `rubric-requirement:${requirement}:${anchor.field}`;
  }
  return `rubric-grade:${requirement}:${encodeURIComponent(anchor.modelKey)}:${anchor.field}`;
}

/**
 * Key used by the shell's comment index. Orphaned migrated comments get a
 * private key so they cannot accidentally fall back to their old numeric
 * path and appear on a different requirement.
 */
export function taskCommentGroupingKey(
  comment: Pick<TaskComment, 'id' | 'fieldPath' | 'fieldAnchor' | 'fieldAnchorState'>,
): string | null {
  if (comment.fieldAnchorState === 'orphaned') return `orphaned:${comment.id}`;
  return taskCommentFieldAnchorKey(comment.fieldAnchor) ?? comment.fieldPath;
}
